import {RuleSeverityBadge} from '@github-ui/code-scanning-shared/components/RuleSeverityBadge'
import type {CopilotFixDialogSubmitOptions} from '@github-ui/copilot-apply-changes-button/CopilotFixDialog'
import {CopilotFixDialog} from '@github-ui/copilot-apply-changes-button/CopilotFixDialog'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {AgentIcon} from '@primer/octicons-react'
import {ActionList, Button, Dialog} from '@primer/react'
import {type FC, useCallback, useMemo, useState} from 'react'
import {getFindingFileLocation, getFindingSeverity, getFindingTitle} from './entity-helpers'
import type {Finding} from './types'

interface FixFindingDialogProps {
  findings: Finding[]
  basePath: string
  modelsUrl?: string | null
  onClose: () => void
}

export const FixFindingDialog: FC<FixFindingDialogProps> = ({findings, basePath, modelsUrl, onClose}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(findings.map(f => f.id)))
  const [taskUrl, setTaskUrl] = useState<string | null>(null)

  const selectedFindings = useMemo(() => findings.filter(f => selectedIds.has(f.id)), [findings, selectedIds])

  const handleToggle = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleSubmit = useCallback(
    async ({createPullRequest, instructions, model}: CopilotFixDialogSubmitOptions) => {
      const body: Record<string, unknown> = {
        create_pull_request: createPullRequest,
        findings: selectedFindings.map(f => ({
          id: f.id,
          message: f.message,
          filePath: f.filePath,
          startLine: f.startLine,
          endLine: f.endLine,
        })),
      }
      if (instructions) body.instructions = instructions
      if (model && model !== 'auto') body.model = model

      const response = await verifiedFetchJSON(`${basePath}/code_review_findings/fix`, {
        method: 'POST',
        body,
      })
      if (!response.ok) {
        let data = null
        try {
          data = await response.json()
        } catch {
          // ignore JSON parse errors
        }
        throw new Error(data?.error || data?.message || 'Failed to create fix task')
      }

      const data = await response.json()
      if (!data?.task_url) {
        throw new Error('No task URL returned')
      }
      setTaskUrl(data.task_url)
      return false // prevent CopilotFixDialog from auto-closing
    },
    [basePath, selectedFindings],
  )

  if (taskUrl) {
    return (
      <Dialog title="Fix with Copilot" onClose={onClose} width="medium">
        <Dialog.Body>
          <p>
            {`Copilot is fixing ${selectedFindings.length} ${selectedFindings.length === 1 ? 'finding' : 'findings'}.`}
          </p>
          <Button as="a" href={taskUrl} leadingVisual={AgentIcon} className="tmp-mt-2">
            View agent session
          </Button>
        </Dialog.Body>
      </Dialog>
    )
  }

  return (
    <CopilotFixDialog
      title="Fix with Copilot"
      onClose={onClose}
      onSubmit={handleSubmit}
      modelsUrl={modelsUrl ?? undefined}
      submitDisabled={selectedIds.size === 0}
      closeOnSubmit={false}
    >
      <div className="tmp-mt-3">
        <p className="tmp-mb-1 text-small text-semibold color-fg-muted">
          {`${selectedIds.size} ${selectedIds.size === 1 ? 'finding' : 'findings'}`}
        </p>
        <ActionList
          showDividers
          role="listbox"
          selectionVariant="multiple"
          aria-label="Findings to fix"
          className="mt-2 border rounded-3"
        >
          {findings.map(f => {
            const severity = getFindingSeverity(f)
            const fileLocation = getFindingFileLocation(f)
            return (
              <ActionList.Item key={f.id} selected={selectedIds.has(f.id)} onSelect={() => handleToggle(f.id)}>
                <span>{getFindingTitle(f)}</span>
                <ActionList.Description variant="block">
                  <div className="d-flex align-center gap-1">
                    {severity && <RuleSeverityBadge severity={severity} />}
                    {fileLocation && (
                      <>
                        <span className="color-fg-muted">{'\u00B7'}</span>
                        <span className="text-mono">{fileLocation}</span>
                      </>
                    )}
                  </div>
                </ActionList.Description>
              </ActionList.Item>
            )
          })}
        </ActionList>
      </div>
    </CopilotFixDialog>
  )
}
